/**
 * UI Store for CallerID Reputation Monitor
 *
 * This store manages global UI state like toasts, modals, and sidebar.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Logger, LogCategory } from '@/utils/logger';
import type { Toast, ToastType, ModalState } from '@/types';
import { generateId } from '@/utils';

// ============================================================================
// Types
// ============================================================================

interface UIState {
  // Toasts
  toasts: Toast[];

  // Modal
  modal: ModalState;

  // Sidebar
  sidebarCollapsed: boolean;

  // Loading overlays
  globalLoading: boolean;
  loadingMessage: string | null;

  // Toast Actions
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  showSuccessToast: (title: string, message?: string) => string;
  showErrorToast: (title: string, message?: string) => string;
  showWarningToast: (title: string, message?: string) => string;
  showInfoToast: (title: string, message?: string) => string;

  // Modal Actions
  openModal: (type: string, data?: unknown) => void;
  closeModal: () => void;

  // Sidebar Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Loading Actions
  setGlobalLoading: (loading: boolean, message?: string) => void;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_TOAST_DURATION = 5000;
const MAX_TOASTS = 5;

// ============================================================================
// Store Implementation
// ============================================================================

export const useUIStore = create<UIState>()(
  devtools(
    (set, get) => ({
      // Initial State
      toasts: [],
      modal: {
        isOpen: false,
        type: null,
        data: undefined,
      },
      sidebarCollapsed: false,
      globalLoading: false,
      loadingMessage: null,

      // Add toast
      addToast: (toast: Omit<Toast, 'id'>) => {
        const id = generateId('toast');

        Logger.debug(LogCategory.UI, `Adding toast: ${toast.type} - ${toast.title}`, 'useUIStore.addToast', {
          id,
          type: toast.type,
        });

        const newToast: Toast = {
          ...toast,
          id,
          duration: toast.duration ?? DEFAULT_TOAST_DURATION,
        };

        set((state) => {
          // Keep only the most recent toasts
          const updatedToasts = [...state.toasts, newToast].slice(-MAX_TOASTS);
          return { toasts: updatedToasts };
        });

        // Auto-remove toast after duration
        if (newToast.duration && newToast.duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, newToast.duration);
        }

        return id;
      },

      // Remove toast
      removeToast: (id: string) => {
        Logger.debug(LogCategory.UI, `Removing toast: ${id}`, 'useUIStore.removeToast');
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        }));
      },

      // Clear all toasts
      clearToasts: () => {
        Logger.debug(LogCategory.UI, 'Clearing all toasts', 'useUIStore.clearToasts');
        set({ toasts: [] });
      },

      // Helper methods for common toast types
      showSuccessToast: (title: string, message?: string) => {
        Logger.info(LogCategory.UI, `Success toast: ${title}`, 'useUIStore.showSuccessToast');
        return get().addToast({ type: 'success', title, message });
      },

      showErrorToast: (title: string, message?: string) => {
        Logger.warn(LogCategory.UI, `Error toast: ${title}`, 'useUIStore.showErrorToast');
        return get().addToast({ type: 'error', title, message, duration: 8000 });
      },

      showWarningToast: (title: string, message?: string) => {
        Logger.info(LogCategory.UI, `Warning toast: ${title}`, 'useUIStore.showWarningToast');
        return get().addToast({ type: 'warning', title, message, duration: 6000 });
      },

      showInfoToast: (title: string, message?: string) => {
        Logger.debug(LogCategory.UI, `Info toast: ${title}`, 'useUIStore.showInfoToast');
        return get().addToast({ type: 'info', title, message });
      },

      // Open modal
      openModal: (type: string, data?: unknown) => {
        Logger.info(LogCategory.UI, `Opening modal: ${type}`, 'useUIStore.openModal', {
          hasData: !!data,
        });

        set({
          modal: {
            isOpen: true,
            type,
            data,
          },
        });
      },

      // Close modal
      closeModal: () => {
        Logger.info(LogCategory.UI, 'Closing modal', 'useUIStore.closeModal', {
          wasType: get().modal.type,
        });

        set({
          modal: {
            isOpen: false,
            type: null,
            data: undefined,
          },
        });
      },

      // Toggle sidebar
      toggleSidebar: () => {
        const newState = !get().sidebarCollapsed;
        Logger.debug(LogCategory.UI, `Toggling sidebar: ${newState}`, 'useUIStore.toggleSidebar');
        set({ sidebarCollapsed: newState });
      },

      // Set sidebar collapsed
      setSidebarCollapsed: (collapsed: boolean) => {
        Logger.debug(LogCategory.UI, `Setting sidebar collapsed: ${collapsed}`, 'useUIStore.setSidebarCollapsed');
        set({ sidebarCollapsed: collapsed });
      },

      // Set global loading
      setGlobalLoading: (loading: boolean, message?: string) => {
        Logger.debug(LogCategory.UI, `Setting global loading: ${loading}`, 'useUIStore.setGlobalLoading', {
          message,
        });

        set({
          globalLoading: loading,
          loadingMessage: loading ? message || null : null,
        });
      },
    }),
    { name: 'uiStore' }
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

export const useToasts = () => useUIStore((state) => state.toasts);
export const useModal = () => useUIStore((state) => state.modal);
export const useSidebarCollapsed = () => useUIStore((state) => state.sidebarCollapsed);
export const useGlobalLoading = () => useUIStore((state) => state.globalLoading);
export const useLoadingMessage = () => useUIStore((state) => state.loadingMessage);

// Action hooks for cleaner component code
export const useToastActions = () => {
  const store = useUIStore();
  return {
    addToast: store.addToast,
    removeToast: store.removeToast,
    clearToasts: store.clearToasts,
    showSuccess: store.showSuccessToast,
    showError: store.showErrorToast,
    showWarning: store.showWarningToast,
    showInfo: store.showInfoToast,
  };
};

export const useModalActions = () => {
  const store = useUIStore();
  return {
    open: store.openModal,
    close: store.closeModal,
  };
};
